import { useCallback, useEffect, useState } from "react";
import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RegistryTable, { STATUS_CHIP, STATUS_LABEL } from "@/components/vault/RegistryTable";
import UploadDialog from "@/components/vault/UploadDialog";
import DocPreviewDialog from "@/components/vault/DocPreviewDialog";
import ComplianceConsole from "@/components/vault/ComplianceConsole";
import DigestsTab from "@/components/vault/DigestsTab";

const DOC_TYPES = ["passport", "visa", "id", "certification", "contract", "other"];

export default function DocumentsPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isHR = ["admin", "hr_manager"].includes(user.role);
  const canUpload = isHR || user.role === "employee";

  const [docs, setDocs] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState("all");
  const [docType, setDocType] = useState("all");
  const [dept, setDept] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const reload = useCallback(() => {
    api.get("/documents").then((r) => setDocs(r.data.documents)).catch(() => {});
  }, []);

  useEffect(() => {
    reload();
    if (isHR) api.get("/employees", { params: { limit: 200 } }).then((r) => setEmployees(r.data.employees)).catch(() => {});
  }, [reload, isHR]);

  const departments = [...new Set((docs || []).map((d) => d.department).filter(Boolean))].sort();
  const filtered = (docs || []).filter((d) =>
    (status === "all" || d.status === status) &&
    (docType === "all" || d.doc_type === docType) &&
    (dept === "all" || d.department === dept));
  const counts = (docs || []).reduce((acc, d) => { acc[d.status] = (acc[d.status] || 0) + 1; return acc; }, {});

  const registry = (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5 flex-wrap">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-none h-9 w-44 text-[13px]" data-testid="documents-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all" className="rounded-none text-[13px]">{t("documents.allStatuses")}</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, l]) => (
              <SelectItem key={k} value={k} className="rounded-none text-[13px]">{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger className="rounded-none h-9 w-40 text-[13px]" data-testid="documents-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all" className="rounded-none text-[13px]">{t("documents.allTypes")}</SelectItem>
            {DOC_TYPES.map((dt) => <SelectItem key={dt} value={dt} className="rounded-none text-[13px]">{t(`documents.type${dt[0].toUpperCase()}${dt.slice(1)}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        {isHR && (
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="rounded-none h-9 w-44 text-[13px]" data-testid="documents-dept-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all" className="rounded-none text-[13px]">{t("documents.allDepartments")}</SelectItem>
              {departments.map((d) => <SelectItem key={d} value={d} className="rounded-none text-[13px]">{d}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-2 flex-wrap" data-testid="documents-status-summary">
          {Object.entries(STATUS_LABEL).map(([key, label]) =>
            counts[key] ? (
              <span key={key} className={`text-[10px] font-mono px-2 py-1 ${STATUS_CHIP[key]}`}>{label}: {counts[key]}</span>
            ) : null)}
        </div>
        <span className="text-[12px] font-mono text-muted-foreground ms-auto" data-testid="documents-count">
          {t("documents.count", { count: filtered.length })}
        </span>
        {canUpload && (
          <Button onClick={() => setUploadOpen(true)} data-testid="upload-doc-btn"
            className="rounded-none h-9 text-[13px] bg-teal-700 hover:bg-teal-800 text-white">
            <Upload className="h-4 w-4" /> {t("documents.uploadDocument")}
          </Button>
        )}
      </div>
      {!docs ? <Skeleton className="h-96 w-full rounded-none" /> : (
        <RegistryTable docs={filtered} user={user} onChanged={reload} onPreview={setPreviewDoc} />
      )}
    </div>
  );

  return (
    <div className="space-y-5 fade-in" data-testid="documents-page">
      {isHR ? (
        <Tabs defaultValue="registry">
          <TabsList className="rounded-none bg-white border border-border h-auto p-0 justify-start">
            {[["registry", t("documents.tabRegistry")], ["console", t("documents.tabConsole")], ["digests", t("documents.tabDigests")]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`documents-tab-${v}`}
                className="rounded-none px-5 py-2.5 text-[13px] data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white data-[state=active]:shadow-none border-r border-border last:border-r-0">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="registry" className="mt-5">{registry}</TabsContent>
          <TabsContent value="console" className="mt-5"><ComplianceConsole /></TabsContent>
          <TabsContent value="digests" className="mt-5"><DigestsTab /></TabsContent>
        </Tabs>
      ) : registry}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} employees={employees} user={user} onSaved={reload} />
      <DocPreviewDialog doc={previewDoc} open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)} />
    </div>
  );
}
